import React from 'react';
import { Card, CardHeader, Badge } from '../ui';
import {
  CodeBlock,
  Callout,
  CollapsibleSection,
  FieldTable,
  Mono,
  SectionHeading,
  SimpleTable,
} from './DocPrimitives';

/* -------------------------------------------------------------------------- */
/*  Shared reference data                                                     */
/* -------------------------------------------------------------------------- */

const SOURCE_LABELS: Record<string, string> = {
  mdi: 'MDI',
  curexa: 'Curexa',
  pcp_health: 'PCP Health',
  authorize_net: 'Authorize.net',
  capture_health: 'Capture Health',
};

interface CatalogEntry {
  type: string;
  sources: string[];
  description: string;
  /** Fields that appear inside `data` for this specific event. */
  data?: { name: string; type: string; description: string }[];
}

interface CatalogGroup {
  category: string;
  label: string;
  blurb: string;
  events: CatalogEntry[];
}

const EVENT_CATALOG: CatalogGroup[] = [
  {
    category: 'patient',
    label: 'Patient events',
    blurb:
      'Fired as customers enter and move through your funnel, before any order exists. Useful for seeding your CRM the moment someone identifies themselves.',
    events: [
      {
        type: 'patient.created',
        sources: ['capture_health'],
        description: 'A new patient record was created for your company, typically at the start of checkout.',
      },
      {
        type: 'patient.updated',
        sources: ['capture_health'],
        description: 'A patient’s contact details or demographics changed.',
        data: [
          { name: 'changedFields', type: 'string[]', description: 'Which fields changed, e.g. ["email", "phoneNumber"].' },
        ],
      },
    ],
  },
  {
    category: 'order',
    label: 'Order events',
    blurb:
      'The commercial lifecycle of an order. `order.created` fires for the initial purchase and again for every subscription refill, so each is independently attributable.',
    events: [
      {
        type: 'order.created',
        sources: ['capture_health', 'authorize_net'],
        description:
          'An order was created and paid for. Emitted for the first purchase and for every recurring refill order.',
      },
      {
        type: 'order.cancelled',
        sources: ['capture_health', 'curexa', 'pcp_health'],
        description: 'The order was cancelled before or during fulfillment.',
        data: [{ name: 'reason', type: 'string | null', description: 'Cancellation reason where one was supplied.' }],
      },
      {
        type: 'order.refunded',
        sources: ['capture_health', 'authorize_net'],
        description: 'The order was refunded in full.',
        data: [
          { name: 'amount', type: 'number', description: 'Amount refunded.' },
          { name: 'currency', type: 'string', description: 'Always "USD".' },
          { name: 'reason', type: 'string | null', description: 'Refund reason where one was supplied.' },
        ],
      },
      {
        type: 'order.partially_refunded',
        sources: ['capture_health', 'authorize_net'],
        description: 'Part of the order was refunded; the balance remains captured.',
        data: [
          { name: 'amount', type: 'number', description: 'Amount refunded in this event.' },
          { name: 'currency', type: 'string', description: 'Always "USD".' },
        ],
      },
    ],
  },
  {
    category: 'clinical',
    label: 'Clinical review events',
    blurb:
      'Sourced from MDI, our telehealth EHR. These tell you where a customer sits in provider review — the most common reason an order stalls between payment and shipment.',
    events: [
      {
        type: 'case.created',
        sources: ['mdi'],
        description: 'A clinical case was opened and is awaiting provider review.',
        data: [{ name: 'caseId', type: 'string', description: 'The MDI case identifier.' }],
      },
      {
        type: 'case.action_required',
        sources: ['mdi'],
        description:
          'The provider needs something from the patient before they can proceed. Use this to trigger your own reminder flows.',
        data: [
          {
            name: 'actionType',
            type: '"drivers_license" | "intro_video" | "file_upload" | "exam"',
            description: 'What the patient has been asked for.',
          },
          { name: 'accessLink', type: 'string | null', description: 'Link the patient uses to complete the request.' },
          { name: 'caseId', type: 'string', description: 'The MDI case identifier.' },
        ],
      },
      {
        type: 'case.completed',
        sources: ['mdi'],
        description: 'The provider finished reviewing the case.',
        data: [{ name: 'caseId', type: 'string', description: 'The MDI case identifier.' }],
      },
      {
        type: 'case.rejected',
        sources: ['mdi'],
        description: 'The case was rejected. Any authorization hold on the order is released.',
        data: [
          { name: 'caseId', type: 'string', description: 'The MDI case identifier.' },
          { name: 'reason', type: 'string | null', description: 'Rejection reason where one was supplied.' },
        ],
      },
      {
        type: 'prescription.submitted',
        sources: ['mdi'],
        description:
          'The provider approved and submitted a prescription. Payment capture (one-time) or subscription creation (recurring) follows immediately.',
        data: [{ name: 'caseId', type: 'string', description: 'The MDI case identifier.' }],
      },
      {
        type: 'prescription.rejected',
        sources: ['mdi'],
        description: 'The prescription was rejected. Any authorization hold on the order is released.',
        data: [{ name: 'caseId', type: 'string', description: 'The MDI case identifier.' }],
      },
    ],
  },
  {
    category: 'payment',
    label: 'Payment events',
    blurb:
      'Sourced from Authorize.net. Every payment event carries `data.transactionId`, which reconciles directly against your gateway statements.',
    events: [
      {
        type: 'payment.authorized',
        sources: ['authorize_net'],
        description: 'A card authorization succeeded. Funds are held but not captured.',
      },
      {
        type: 'payment.captured',
        sources: ['authorize_net'],
        description: 'Funds were captured for a one-time purchase or an initial order.',
      },
      {
        type: 'payment.failed',
        sources: ['authorize_net'],
        description: 'A charge attempt was declined or errored.',
        data: [{ name: 'failureReason', type: 'string | null', description: 'Gateway or platform failure detail.' }],
      },
      {
        type: 'payment.voided',
        sources: ['authorize_net'],
        description: 'A transaction was voided before settlement.',
      },
      {
        type: 'payment.refunded',
        sources: ['authorize_net'],
        description: 'A settled transaction was refunded.',
        data: [
          { name: 'refundTransactionId', type: 'string | null', description: 'Gateway id of the refund transaction.' },
          { name: 'totalRefunded', type: 'number | null', description: 'Cumulative amount refunded on the order.' },
          { name: 'isFullRefund', type: 'boolean | null', description: 'Whether the order is now fully refunded.' },
        ],
      },
      {
        type: 'payment.hold_created',
        sources: ['authorize_net'],
        description: 'An authorization hold was placed on the card pending provider review.',
      },
      {
        type: 'payment.hold_released',
        sources: ['authorize_net'],
        description: 'An authorization hold was released without capture.',
      },
      {
        type: 'payment.hold_failed',
        sources: ['authorize_net'],
        description: 'An attempt to place or release an authorization hold failed.',
        data: [{ name: 'failureReason', type: 'string | null', description: 'Why the hold operation failed.' }],
      },
      {
        type: 'payment.fraud_held',
        sources: ['authorize_net'],
        description: 'The transaction was held by the fraud detection suite for manual review.',
      },
      {
        type: 'payment.fraud_approved',
        sources: ['authorize_net'],
        description: 'A held transaction was approved after fraud review.',
      },
      {
        type: 'payment.fraud_declined',
        sources: ['authorize_net'],
        description: 'A held transaction was declined after fraud review.',
      },
    ],
  },
  {
    category: 'subscription',
    label: 'Subscription events',
    blurb:
      'Recurring billing, sourced from Authorize.net. `subscription.charged` is the event most affiliate programmes pay out on — it carries `data.paymentNumber` so you can distinguish an initial charge from a rebill.',
    events: [
      {
        type: 'subscription.created',
        sources: ['authorize_net'],
        description: 'A recurring billing subscription was created for the order.',
        data: [
          { name: 'intervalMonths', type: 'number | null', description: 'Months between charges.' },
          { name: 'totalOccurrences', type: 'number | null', description: 'Total scheduled charges.' },
        ],
      },
      {
        type: 'subscription.updated',
        sources: ['authorize_net'],
        description: 'A subscription schedule, amount or payment method changed.',
      },
      {
        type: 'subscription.charged',
        sources: ['authorize_net'],
        description: 'A recurring charge succeeded.',
        data: [
          { name: 'paymentNumber', type: 'number', description: 'Which billing cycle this is. 1 is the initial charge; 2+ are rebills.' },
          { name: 'amount', type: 'number', description: 'Amount charged.' },
          { name: 'transactionId', type: 'string', description: 'Gateway transaction id for this charge.' },
        ],
      },
      {
        type: 'subscription.charge_failed',
        sources: ['authorize_net'],
        description: 'A recurring charge was declined.',
        data: [{ name: 'failureReason', type: 'string | null', description: 'Decline reason where available.' }],
      },
      {
        type: 'subscription.creation_failed',
        sources: ['authorize_net'],
        description: 'We could not create the subscription after the prescription was approved.',
        data: [{ name: 'failureReason', type: 'string | null', description: 'Why creation failed.' }],
      },
      {
        type: 'subscription.suspended',
        sources: ['authorize_net'],
        description: 'The subscription was suspended, usually after repeated payment failures.',
      },
      {
        type: 'subscription.cancelled',
        sources: ['authorize_net', 'capture_health'],
        description: 'The subscription was cancelled or terminated. No further charges will occur.',
      },
      {
        type: 'subscription.expiring',
        sources: ['authorize_net'],
        description: 'The subscription is approaching its final scheduled occurrence.',
      },
      {
        type: 'subscription.expired',
        sources: ['authorize_net'],
        description: 'The subscription completed all of its scheduled occurrences.',
      },
    ],
  },
  {
    category: 'fulfillment',
    label: 'Pharmacy fulfillment events',
    blurb:
      'Sourced from the pharmacy filling the order — Curexa or PCP Health. An order can fork across multiple pharmacies, so you may receive more than one fulfillment chain per order.',
    events: [
      {
        type: 'fulfillment.submitted',
        sources: ['capture_health'],
        description: 'The order was submitted to a pharmacy for fulfillment.',
        data: [{ name: 'pharmacyOrderId', type: 'string | null', description: 'The pharmacy’s own reference for the order.' }],
      },
      {
        type: 'fulfillment.received',
        sources: ['pcp_health', 'curexa'],
        description: 'The pharmacy acknowledged receipt of the order.',
      },
      {
        type: 'fulfillment.in_progress',
        sources: ['curexa', 'pcp_health'],
        description: 'The pharmacy started filling the order.',
      },
      {
        type: 'fulfillment.warning',
        sources: ['pcp_health'],
        description: 'The pharmacy raised a non-fatal warning about the order.',
        data: [
          { name: 'reasonCode', type: 'string | null', description: 'Pharmacy warning code.' },
          { name: 'reasonDescription', type: 'string | null', description: 'Human-readable explanation.' },
        ],
      },
      {
        type: 'fulfillment.failed',
        sources: ['capture_health', 'curexa', 'pcp_health'],
        description: 'The order could not be submitted to, or was rejected by, the pharmacy.',
        data: [{ name: 'failureReason', type: 'string | null', description: 'Why fulfillment failed.' }],
      },
      {
        type: 'fulfillment.cancelled',
        sources: ['curexa', 'pcp_health'],
        description: 'The pharmacy cancelled the fulfillment.',
      },
    ],
  },
  {
    category: 'shipment',
    label: 'Shipping events',
    blurb:
      'Carrier milestones. Orders can ship in multiple packages, so treat `data.trackingNumber` as the key rather than assuming one shipment per order.',
    events: [
      {
        type: 'shipment.shipped',
        sources: ['curexa', 'pcp_health'],
        description: 'A shipment was created and handed to the carrier.',
      },
      {
        type: 'shipment.in_transit',
        sources: ['curexa', 'pcp_health'],
        description: 'The carrier scanned the shipment and it is moving.',
      },
      {
        type: 'shipment.out_for_delivery',
        sources: ['curexa', 'pcp_health'],
        description: 'The shipment is out for delivery to the patient.',
      },
      {
        type: 'shipment.delivered',
        sources: ['curexa', 'pcp_health'],
        description: 'The shipment was delivered.',
      },
    ],
  },
];

const SHIPMENT_DATA_FIELDS = [
  { name: 'trackingNumber', type: 'string | null', description: 'Carrier tracking number for this package.' },
  { name: 'carrier', type: 'string | null', description: 'Carrier code, e.g. "usps", "ups", "fedex".' },
  { name: 'shippingMethod', type: 'string | null', description: 'Service level the package shipped on.' },
  { name: 'estimatedDeliveryDate', type: 'string | null', description: 'Estimated delivery date where the carrier supplies one.' },
];

const PAYMENT_DATA_FIELDS = [
  { name: 'transactionId', type: 'string | null', description: 'Authorize.net transaction id.' },
  { name: 'amount', type: 'number | null', description: 'Amount in dollars.' },
  { name: 'currency', type: 'string', description: 'Always "USD".' },
  { name: 'authCode', type: 'string | null', description: 'Gateway authorization code.' },
  { name: 'transactionStatus', type: 'string | null', description: 'Raw gateway status, e.g. "settledSuccessfully".' },
];

const SUBSCRIPTION_DATA_FIELDS = [
  { name: 'subscriptionId', type: 'string | null', description: 'Authorize.net subscription id.' },
  { name: 'amount', type: 'number | null', description: 'Charge amount for this cycle.' },
  { name: 'currency', type: 'string', description: 'Always "USD".' },
  { name: 'transactionId', type: 'string | null', description: 'Transaction id for this cycle’s charge.' },
  { name: 'paymentNumber', type: 'number | null', description: 'Which billing cycle this is (1 = initial charge).' },
  { name: 'intervalMonths', type: 'number | null', description: 'Months between charges.' },
  { name: 'totalOccurrences', type: 'number | null', description: 'Total scheduled charges.' },
];

/* -------------------------------------------------------------------------- */
/*  Sample payloads                                                           */
/* -------------------------------------------------------------------------- */

const SAMPLE_ENVELOPE = `{
  "id": "evt_9f2c1a7b4e6d8035a1c4f7b2d9e03516",
  "type": "subscription.charged",
  "apiVersion": "2026-09-01",
  "source": "authorize_net",
  "createdAt": "2026-09-03T14:22:07.418Z",
  "occurredAt": "2026-09-03T14:22:06.901Z",
  "livemode": true,
  "company": {
    "id": "3f9a6c21-8b74-4d0e-9a55-2c1e7d8b4f60",
    "name": "Northstar Health"
  },
  "patient": {
    "id": "b81d4e27-6a3f-4c95-8e12-7f0b5d9a3c48",
    "firstName": "Dana",
    "lastName": "Whitfield",
    "email": "dana.whitfield@example.com",
    "phoneNumber": "+15125550142",
    "dateOfBirth": "1988-04-17T00:00:00.000Z",
    "gender": "female",
    "createdAt": "2026-01-12T18:04:55.220Z",
    "externalIds": {
      "mdiPatientId": "mdi_84f21c",
      "authorizeNetCustomerProfileId": "1927463011"
    }
  },
  "order": {
    "id": "c47e9b02-5d81-4a3e-b0f6-92a7c3e15d84",
    "orderCode": "NS-4821",
    "status": "active_subscription",
    "currency": "USD",
    "chargeAmount": 249,
    "shippingCharge": 0,
    "taxedAmount": 0,
    "trialAmount": null,
    "isSubscription": true,
    "subscriptionIntervalMonths": 3,
    "subscriptionOccurrences": 4,
    "couponCode": null,
    "createdAt": "2026-01-12T18:09:31.006Z",
    "items": [
      {
        "id": "a12f7c39-4e6b-48d1-9c25-73b0e8f4a916",
        "offeringId": "6d3b8f14-2a97-4e50-b8c1-05f9d27a6e33",
        "name": "Semaglutide",
        "description": "Compounded semaglutide, 3 month supply",
        "dosage": "0.5 mg",
        "quantity": 3,
        "monthSupply": 3,
        "price": 249,
        "pharmacyId": "e0a45b72-9c18-4d63-a2f7-14b8e6c0d597"
      }
    ],
    "shippingAddress": {
      "address1": "1904 Cedar Ridge Dr",
      "address2": "Apt 5B",
      "city": "Austin",
      "state": "TX",
      "postalCode": "78745"
    },
    "externalIds": {
      "mdiCaseId": "case_5a71bd",
      "authorizeNetTransactionId": "80147295613",
      "authorizeNetSubscriptionId": "9184627"
    }
  },
  "attribution": {
    "utmSource": "affiliate",
    "utmMedium": "cpa",
    "utmCampaign": "q3-glp1-push",
    "utmContent": "sidebar-banner-b",
    "utmTerm": null,
    "affiliateId": "aff_44192",
    "campaignCode": "NS-Q3-GLP1",
    "couponCode": "SAVE20",
    "referrer": "https://healthblog.example.com/glp1-review",
    "landingPage": "https://checkout.northstar.health/start?utm_source=affiliate&utm_campaign=q3-glp1-push",
    "visitorId": "vis_7d2e9a01c4b5",
    "leadId": "f3c81a29-6b47-4e0d-95a2-8c17d4e6b035",
    "intakeSessionId": "2b9e4f71-8d05-4c36-a17f-6e93b0c5d842",
    "firstSeenAt": "2026-01-12T17:51:02.117Z",
    "convertedAt": "2026-01-12T18:09:31.006Z"
  },
  "data": {
    "subscriptionId": "9184627",
    "amount": 249,
    "currency": "USD",
    "transactionId": "80147295613",
    "paymentNumber": 2,
    "intervalMonths": 3,
    "totalOccurrences": 4
  }
}`;

const SAMPLE_SHIPMENT = `{
  "id": "evt_2d7f4c9018be35a7c6019d24f8b3e5a1",
  "type": "shipment.out_for_delivery",
  "apiVersion": "2026-09-01",
  "source": "curexa",
  "createdAt": "2026-09-04T16:03:44.902Z",
  "occurredAt": "2026-09-04T16:03:44.902Z",
  "livemode": true,
  "company": { "id": "3f9a6c21-8b74-4d0e-9a55-2c1e7d8b4f60", "name": "Northstar Health" },
  "patient": { "id": "b81d4e27-6a3f-4c95-8e12-7f0b5d9a3c48", "firstName": "Dana", "lastName": "Whitfield", "email": "dana.whitfield@example.com", "phoneNumber": "+15125550142", "...": "…" },
  "order": { "id": "c47e9b02-5d81-4a3e-b0f6-92a7c3e15d84", "orderCode": "NS-4821", "...": "…" },
  "attribution": { "affiliateId": "aff_44192", "utmCampaign": "q3-glp1-push", "...": "…" },
  "data": {
    "trackingNumber": "9405511899223197428490",
    "carrier": "usps",
    "shippingMethod": "Priority Mail"
  }
}`;

const SAMPLE_HEADERS = `POST /webhooks/capture-health HTTP/1.1
Host: api.yourcompany.com
Content-Type: application/json
User-Agent: CaptureHealth-Webhooks/1.0
Capture-Signature: t=1772806927,v1=8f1d0a7e5c93b264fa07e18d3b95c6021ad4e7f83c9b0d15e6a247fb8390c4d2
Capture-Timestamp: 1772806927
Capture-Event-Id: evt_9f2c1a7b4e6d8035a1c4f7b2d9e03516
Capture-Event-Type: subscription.charged
Capture-Delivery-Id: 7a3e91c4-58bd-4f06-92e1-c4b78d0a5f36
Capture-Delivery-Attempt: 1
Capture-Api-Version: 2026-09-01`;

const NODE_VERIFY = `const crypto = require('crypto');
const express = require('express');

const app = express();

// IMPORTANT: verify against the RAW body. A parsed-and-restringified body will
// not byte-match what we signed.
app.post(
  '/webhooks/capture-health',
  express.raw({ type: 'application/json' }),
  (req, res) => {
    const header = req.get('Capture-Signature') || '';
    const secret = process.env.CAPTURE_WEBHOOK_SECRET;

    const parts = Object.fromEntries(
      header.split(',').map((part) => part.split('='))
    );
    const timestamp = Number(parts.t);

    // Reject anything older than five minutes to blunt replay attacks.
    if (!timestamp || Math.abs(Date.now() / 1000 - timestamp) > 300) {
      return res.status(400).send('Stale or missing timestamp');
    }

    const expected = crypto
      .createHmac('sha256', secret)
      .update(timestamp + '.' + req.body.toString('utf8'))
      .digest('hex');

    const a = Buffer.from(expected);
    const b = Buffer.from(parts.v1 || '');

    if (a.length !== b.length || !crypto.timingSafeEqual(a, b)) {
      return res.status(400).send('Invalid signature');
    }

    const event = JSON.parse(req.body.toString('utf8'));

    // Acknowledge immediately, then process out of band. We time out after 10s.
    res.status(200).send('ok');
    enqueueForProcessing(event);
  }
);`;

const PYTHON_VERIFY = `import hashlib
import hmac
import json
import os
import time

from flask import Flask, request

app = Flask(__name__)
SECRET = os.environ["CAPTURE_WEBHOOK_SECRET"].encode()


@app.post("/webhooks/capture-health")
def capture_webhook():
    header = request.headers.get("Capture-Signature", "")
    parts = dict(part.split("=", 1) for part in header.split(",") if "=" in part)

    try:
        timestamp = int(parts["t"])
    except (KeyError, ValueError):
        return "Missing timestamp", 400

    # Reject anything older than five minutes to blunt replay attacks.
    if abs(time.time() - timestamp) > 300:
        return "Stale timestamp", 400

    # Verify against the RAW body, not a re-serialized dict.
    raw = request.get_data()
    expected = hmac.new(
        SECRET, f"{timestamp}.".encode() + raw, hashlib.sha256
    ).hexdigest()

    if not hmac.compare_digest(expected, parts.get("v1", "")):
        return "Invalid signature", 400

    event = json.loads(raw)
    enqueue_for_processing(event)  # Acknowledge fast; process out of band.
    return "ok", 200`;

const AFFILIATE_EXAMPLE = `// Attribute every dollar back to the click that produced it.
function handleEvent(event) {
  const affiliate = event.attribution?.affiliateId
    ?? event.attribution?.utmSource;

  if (!affiliate) return; // Organic / direct traffic.

  switch (event.type) {
    // Initial conversion — pay the acquisition bounty.
    case 'order.created':
      ledger.recordConversion({
        affiliate,
        campaign: event.attribution.utmCampaign,
        creative: event.attribution.utmContent,
        orderId: event.order.id,
        customerId: event.patient.id,
        amount: event.order.chargeAmount,
        occurredAt: event.occurredAt,
      });
      break;

    // Every rebill — pay recurring commission. paymentNumber > 1 is a renewal.
    case 'subscription.charged':
      ledger.recordRecurringCommission({
        affiliate,
        orderId: event.order.id,
        cycle: event.data.paymentNumber,
        amount: event.data.amount,
        transactionId: event.data.transactionId,
      });
      break;

    // Claw back a commission already paid.
    case 'order.refunded':
    case 'order.partially_refunded':
      ledger.recordClawback({
        affiliate,
        orderId: event.order.id,
        amount: event.data.amount,
      });
      break;
  }
}`;

/* -------------------------------------------------------------------------- */
/*  Sections                                                                  */
/* -------------------------------------------------------------------------- */

export const WebhookOverview: React.FC = () => (
  <Card>
    <CardHeader title="Overview" subtitle="Real-time telemetry for every customer in your funnel" />

    <div className="space-y-6">
      <p className="text-secondary-600 dark:text-secondary-400">
        Capture Health orchestrates a customer’s journey across several third-party systems: MDI for
        clinical review, Authorize.net for payments and recurring billing, and Curexa or PCP Health for
        pharmacy fulfillment and shipping. Each of those systems calls us back as things happen.
        Webhooks forward that stream on to you — normalised into one consistent shape, so you integrate
        against a single contract instead of four vendor contracts.
      </p>

      <Callout title="Every event names the customer">
        <p>
          Whatever the trigger, the payload always tells you <em>which</em> of your customers it concerns:
          our internal patient id, plus their first name, last name, email and phone number. You never
          have to make a second call to work out who an event is about.
        </p>
      </Callout>

      <div>
        <SectionHeading>What you get</SectionHeading>
        <div className="mt-3">
          <SimpleTable
            headers={['Signal', 'Events', 'Typical use']}
            rows={[
              [
                'Customer acquisition',
                <Mono>patient.created</Mono>,
                'Seed your CRM the moment someone identifies themselves.',
              ],
              [
                'Revenue',
                <span>
                  <Mono>order.created</Mono>, <Mono>subscription.charged</Mono>, <Mono>payment.captured</Mono>
                </span>,
                'Recognise revenue and trigger affiliate payouts in real time.',
              ],
              [
                'Churn & dunning',
                <span>
                  <Mono>subscription.charge_failed</Mono>, <Mono>subscription.cancelled</Mono>
                </span>,
                'Fire win-back campaigns and reverse affiliate commissions.',
              ],
              [
                'Order status',
                <span>
                  <Mono>case.*</Mono>, <Mono>fulfillment.*</Mono>, <Mono>shipment.*</Mono>
                </span>,
                'Power your own order-tracking UI and proactive support.',
              ],
            ]}
          />
        </div>
      </div>

      <div>
        <SectionHeading>How it works</SectionHeading>
        <div className="mt-3 space-y-4">
          {[
            {
              title: 'Add an endpoint',
              body: 'In Configurations → Webhooks, add an HTTPS URL and pick the events you want. We generate a signing secret and show it once.',
            },
            {
              title: 'Something happens upstream',
              body: 'A provider approves a prescription, a rebill settles, a package goes out for delivery — whichever system owns that step calls us.',
            },
            {
              title: 'We normalise and sign',
              body: 'The vendor payload is mapped onto a Capture Health event type, enriched with the patient, order and marketing attribution, then signed with your secret.',
            },
            {
              title: 'We POST it to you',
              body: 'Your endpoint receives the event, usually within a second or two. Non-2xx responses are retried with exponential backoff for roughly eight hours.',
            },
          ].map((step, index) => (
            <div key={step.title} className="flex gap-4">
              <div className="shrink-0 w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 flex items-center justify-center text-sm font-semibold">
                {index + 1}
              </div>
              <div>
                <h4 className="font-medium text-secondary-900 dark:text-secondary-100">{step.title}</h4>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionHeading>Delivery guarantees</SectionHeading>
        <div className="mt-3">
          <SimpleTable
            headers={['Property', 'Behaviour']}
            rows={[
              ['Transport', 'HTTPS POST with a JSON body.'],
              ['Ordering', <span>Not guaranteed. Use <Mono>occurredAt</Mono> to order events yourself.</span>],
              ['Delivery semantics', <span>At least once. Deduplicate on the event <Mono>id</Mono>.</span>],
              ['Timeout', '10 seconds per attempt.'],
              ['Success', 'Any 2xx status. The response body is ignored.'],
              ['Retries', '5 retries over ~8 hours on 5xx, 408, 429 or a connection error.'],
              ['No retry', 'Other 4xx responses — we treat those as "this request is wrong", not "try later".'],
            ]}
          />
        </div>
      </div>
    </div>
  </Card>
);

export const WebhookConfiguration: React.FC = () => (
  <Card>
    <CardHeader title="Configuration" subtitle="Setting up an endpoint from the client portal" />

    <div className="space-y-6">
      <p className="text-secondary-600 dark:text-secondary-400">
        Webhook endpoints are managed under <strong>Configurations → Webhooks</strong>. You can register
        several — one for production, one for a staging receiver, one narrowly scoped to a partner — and
        each has its own signing secret, subscription list and health history.
      </p>

      <div>
        <SectionHeading>Endpoint requirements</SectionHeading>
        <div className="mt-3">
          <SimpleTable
            headers={['Requirement', 'Detail']}
            rows={[
              ['Scheme', <span>HTTPS only. Plain <Mono>http://</Mono> URLs are rejected.</span>],
              ['Reachability', 'Publicly routable. Loopback and private-range hosts are rejected.'],
              ['Response', 'Any 2xx within 10 seconds.'],
              ['Redirects', 'Not followed — point us at the final URL.'],
            ]}
          />
        </div>
      </div>

      <div>
        <SectionHeading>Choosing events</SectionHeading>
        <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
          The default, <strong>Send all events</strong>, subscribes you to the whole catalog including
          event types we add later — which is what most integrations want. Selecting events explicitly
          pins you to that list, so you will need to come back and opt into anything new.
        </p>
      </div>

      <div>
        <SectionHeading>Testing</SectionHeading>
        <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
          <strong>Send test</strong> posts a synthetic event to your endpoint and shows you the status
          code, response body and round-trip time straight away. Test events carry{' '}
          <Mono>data.test: true</Mono> — branch on it if you want your receiver to ignore them.
        </p>
      </div>

      <div>
        <SectionHeading>Delivery history and replay</SectionHeading>
        <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
          Every attempt is recorded with the exact body we sent and the response we got. If your
          receiver was down, expand <strong>Recent deliveries</strong> and replay individual events
          rather than reconciling by hand.
        </p>
      </div>

      <Callout variant="warning" title="Endpoints that keep failing get switched off">
        <p>
          After 25 consecutive failures we disable the endpoint and stop queueing events for it, so a
          dead receiver does not accumulate an unbounded backlog. Fix the receiver, then re-enable it
          from the portal — re-enabling resets the failure count.
        </p>
      </Callout>
    </div>
  </Card>
);

export const WebhookSecurity: React.FC = () => (
  <Card>
    <CardHeader title="Security" subtitle="Verifying that a request really came from Capture Health" />

    <div className="space-y-6">
      <p className="text-secondary-600 dark:text-secondary-400">
        Your endpoint is a public URL, so anyone can POST to it. Every request we send carries an HMAC
        signature computed with your endpoint’s secret. <strong>Verify it before you trust the body.</strong>
      </p>

      <div>
        <SectionHeading>Request headers</SectionHeading>
        <div className="mt-3">
          <CodeBlock code={SAMPLE_HEADERS} language="http" title="Example request" />
        </div>
        <div className="mt-3">
          <SimpleTable
            headers={['Header', 'Meaning']}
            rows={[
              [<Mono>Capture-Signature</Mono>, <span>The signature, as <Mono>t=&lt;unix-seconds&gt;,v1=&lt;hex&gt;</Mono>.</span>],
              [<Mono>Capture-Timestamp</Mono>, 'When we signed the request, in Unix seconds.'],
              [<Mono>Capture-Event-Id</Mono>, <span>Matches <Mono>id</Mono> in the body. Use it to deduplicate.</span>],
              [<Mono>Capture-Event-Type</Mono>, 'Lets you route without parsing the body.'],
              [<Mono>Capture-Delivery-Id</Mono>, 'Unique per attempt chain; quote it when contacting support.'],
              [<Mono>Capture-Delivery-Attempt</Mono>, '1 on the first try, incrementing on each retry.'],
              [<Mono>Capture-Api-Version</Mono>, 'The payload contract version.'],
            ]}
          />
        </div>
      </div>

      <div>
        <SectionHeading>The algorithm</SectionHeading>
        <ol className="mt-3 space-y-2 text-sm text-secondary-600 dark:text-secondary-400 list-decimal list-inside">
          <li>
            Parse <Mono>Capture-Signature</Mono> into its <Mono>t</Mono> and <Mono>v1</Mono> parts.
          </li>
          <li>
            Reject the request if <Mono>t</Mono> is more than five minutes from your own clock.
          </li>
          <li>
            Build the signed string: the timestamp, a literal <Mono>.</Mono>, then the{' '}
            <strong>raw request body</strong>.
          </li>
          <li>
            Compute <Mono>HMAC-SHA256</Mono> over it with your endpoint secret and hex-encode the result.
          </li>
          <li>
            Compare against <Mono>v1</Mono> using a constant-time comparison.
          </li>
        </ol>
      </div>

      <Callout variant="warning" title="Sign the raw bytes">
        <p>
          Verify against the body exactly as it arrived. Most frameworks parse JSON before your handler
          runs; re-serialising that object produces different bytes and the signature will not match.
          In Express use <Mono>express.raw()</Mono>; in Flask use <Mono>request.get_data()</Mono>.
        </p>
      </Callout>

      <div>
        <SectionHeading>Reference implementations</SectionHeading>
        <div className="mt-3 space-y-4">
          <CodeBlock code={NODE_VERIFY} language="javascript" title="Node.js / Express" />
          <CodeBlock code={PYTHON_VERIFY} language="python" title="Python / Flask" />
        </div>
      </div>

      <div>
        <SectionHeading>Rotating a secret</SectionHeading>
        <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
          Rotating issues a new secret and invalidates the old one immediately — there is no overlap
          window. To rotate without dropping events, have your receiver accept either of two secrets,
          deploy that, rotate in the portal, then remove the old secret on your next deploy.
        </p>
      </div>
    </div>
  </Card>
);

export const WebhookPayload: React.FC = () => (
  <Card>
    <CardHeader title="Payload" subtitle="The envelope every event shares" />

    <div className="space-y-6">
      <p className="text-secondary-600 dark:text-secondary-400">
        Every event, whatever triggered it, arrives in the same envelope. The top level tells you what
        happened and who it happened to; <Mono>data</Mono> carries the fields specific to that event
        type. Write your receiver against the envelope once and new event types slot in without changes.
      </p>

      <div>
        <SectionHeading>Top level</SectionHeading>
        <div className="mt-3">
          <FieldTable
            fields={[
              { name: 'id', type: 'string', required: true, description: 'Unique event id (evt_…). Use it to deduplicate — delivery is at-least-once.' },
              { name: 'type', type: 'string', required: true, description: 'The event type, e.g. "subscription.charged".' },
              { name: 'apiVersion', type: 'string', required: true, description: 'Payload contract version, e.g. "2026-09-01".' },
              { name: 'source', type: 'string', required: true, description: 'Which system triggered it: mdi, curexa, pcp_health, authorize_net or capture_health.' },
              { name: 'createdAt', type: 'ISO 8601', required: true, description: 'When we generated the event.' },
              { name: 'occurredAt', type: 'ISO 8601', required: true, description: 'When the underlying thing actually happened. Order events by this, not by arrival.' },
              { name: 'livemode', type: 'boolean', required: true, description: 'False for events generated in a non-production environment.' },
              { name: 'company', type: 'object', required: true, description: 'Your company id and name.' },
              { name: 'patient', type: 'object | null', required: false, description: 'The customer this event concerns. Null only for events with no patient context.' },
              { name: 'order', type: 'object | null', required: false, description: 'The order this event concerns. Null for patient-only events.' },
              { name: 'attribution', type: 'object | null', required: false, description: 'Marketing attribution for the visit that produced the order. Null when the order has no tracked session.' },
              { name: 'data', type: 'object', required: true, description: 'Fields specific to this event type. See the event catalog.' },
            ]}
          />
        </div>
      </div>

      <div>
        <SectionHeading>patient</SectionHeading>
        <div className="mt-3">
          <FieldTable
            fields={[
              { name: 'id', type: 'uuid', required: true, description: 'Capture Health patient id. Stable for the life of the customer — key your records on this.' },
              { name: 'firstName', type: 'string | null', required: false, description: 'Given name.' },
              { name: 'lastName', type: 'string | null', required: false, description: 'Family name.' },
              { name: 'email', type: 'string | null', required: false, description: 'Email address.' },
              { name: 'phoneNumber', type: 'string | null', required: false, description: 'Phone number.' },
              { name: 'dateOfBirth', type: 'ISO 8601 | null', required: false, description: 'Date of birth.' },
              { name: 'gender', type: 'string | null', required: false, description: 'Gender as captured at intake.' },
              { name: 'createdAt', type: 'ISO 8601 | null', required: false, description: 'When the patient record was created.' },
              { name: 'externalIds.mdiPatientId', type: 'string | null', required: false, description: 'The patient’s id in MDI.' },
              { name: 'externalIds.authorizeNetCustomerProfileId', type: 'string | null', required: false, description: 'The customer profile id in Authorize.net.' },
            ]}
          />
        </div>
      </div>

      <div>
        <SectionHeading>order</SectionHeading>
        <div className="mt-3">
          <FieldTable
            fields={[
              { name: 'id', type: 'uuid', required: true, description: 'Capture Health order id.' },
              { name: 'orderCode', type: 'string | null', required: false, description: 'Human-readable order reference shown to the customer.' },
              { name: 'status', type: 'string', required: true, description: 'Coarse lifecycle state: pending, processing, active_subscription, cancelled or refunded.' },
              { name: 'currency', type: 'string', required: true, description: 'Always "USD".' },
              { name: 'chargeAmount', type: 'number | null', required: false, description: 'Total charged per billing cycle, including shipping.' },
              { name: 'shippingCharge', type: 'number | null', required: false, description: 'Shipping component of the charge.' },
              { name: 'taxedAmount', type: 'number | null', required: false, description: 'Tax component of the charge.' },
              { name: 'trialAmount', type: 'number | null', required: false, description: 'Discounted first-cycle amount when a coupon or promotion applied.' },
              { name: 'isSubscription', type: 'boolean', required: true, description: 'Whether this order bills recurrently.' },
              { name: 'subscriptionIntervalMonths', type: 'number | null', required: false, description: 'Months between charges.' },
              { name: 'subscriptionOccurrences', type: 'number | null', required: false, description: 'Total scheduled charges.' },
              { name: 'couponCode', type: 'string | null', required: false, description: 'Coupon applied to the order.' },
              { name: 'createdAt', type: 'ISO 8601 | null', required: false, description: 'When the order was created.' },
              { name: 'items', type: 'object[]', required: true, description: 'Line items: id, offeringId, name, description, dosage, quantity, monthSupply, price, pharmacyId.' },
              { name: 'shippingAddress', type: 'object | null', required: false, description: 'address1, address2, city, state, postalCode.' },
              { name: 'externalIds', type: 'object', required: true, description: 'mdiCaseId, authorizeNetTransactionId, authorizeNetSubscriptionId.' },
            ]}
          />
        </div>
      </div>

      <div>
        <SectionHeading>Full example</SectionHeading>
        <div className="mt-3">
          <CodeBlock code={SAMPLE_ENVELOPE} language="json" title="subscription.charged" />
        </div>
      </div>

      <div>
        <SectionHeading>A leaner example</SectionHeading>
        <p className="mt-2 mb-3 text-sm text-secondary-600 dark:text-secondary-400">
          Shipping events share the identical envelope; only <Mono>data</Mono> differs. Fields are
          elided below for brevity — the real payload always sends them in full.
        </p>
        <CodeBlock code={SAMPLE_SHIPMENT} language="json" title="shipment.out_for_delivery" />
      </div>
    </div>
  </Card>
);

export const WebhookAttribution: React.FC = () => (
  <Card>
    <CardHeader title="Attribution" subtitle="Tying revenue back to the campaign that produced it" />

    <div className="space-y-6">
      <p className="text-secondary-600 dark:text-secondary-400">
        Every event that resolves to an order carries an <Mono>attribution</Mono> block describing the
        visit that produced it. Crucially it is present on <em>every</em> such event, not just the
        conversion — so a rebill nine months later still names the affiliate who sent that customer,
        and you can pay recurring commission without joining back to your own conversion table.
      </p>

      <div>
        <SectionHeading>Fields</SectionHeading>
        <div className="mt-3">
          <FieldTable
            fields={[
              { name: 'affiliateId', type: 'string | null', required: false, description: 'Affiliate identifier you passed when creating the checkout session.' },
              { name: 'campaignCode', type: 'string | null', required: false, description: 'Campaign code you passed when creating the checkout session.' },
              { name: 'utmSource', type: 'string | null', required: false, description: 'utm_source from the landing URL.' },
              { name: 'utmMedium', type: 'string | null', required: false, description: 'utm_medium from the landing URL.' },
              { name: 'utmCampaign', type: 'string | null', required: false, description: 'utm_campaign from the landing URL.' },
              { name: 'utmContent', type: 'string | null', required: false, description: 'utm_content — usually the specific creative or placement.' },
              { name: 'utmTerm', type: 'string | null', required: false, description: 'utm_term from the landing URL.' },
              { name: 'couponCode', type: 'string | null', required: false, description: 'Coupon the customer applied. Often doubles as an affiliate code.' },
              { name: 'referrer', type: 'string | null', required: false, description: 'HTTP referrer of the landing page.' },
              { name: 'landingPage', type: 'string | null', required: false, description: 'The full landing URL, query string included.' },
              { name: 'visitorId', type: 'string | null', required: false, description: 'Browser-scoped id that links repeat visits by the same device.' },
              { name: 'leadId', type: 'uuid | null', required: false, description: 'Intake lead id — matches the Insights page and its CSV export.' },
              { name: 'intakeSessionId', type: 'uuid | null', required: false, description: 'Checkout session id, present when you created the session through the Checkout API.' },
              { name: 'firstSeenAt', type: 'ISO 8601 | null', required: false, description: 'When the visitor first landed. The click timestamp, for attribution windows.' },
              { name: 'convertedAt', type: 'ISO 8601 | null', required: false, description: 'When the visit converted into an order.' },
            ]}
          />
        </div>
      </div>

      <div>
        <SectionHeading>Where the values come from</SectionHeading>
        <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
          We stitch attribution from two places. UTM parameters, referrer and landing page are observed
          on the hosted intake portal from the real landing URL. <Mono>affiliateId</Mono> and{' '}
          <Mono>campaignCode</Mono> come from the <Mono>campaign</Mono> object you send when creating a
          checkout session server-side. If you drive traffic through your own storefront, pass those on{' '}
          <Mono>POST /partner/checkout</Mono> and they will flow through to every downstream event.
        </p>
      </div>

      <Callout title="You own the payout logic">
        <p>
          Capture Health does not calculate or pay commissions. We give you the attributed event stream;
          how you rate, hold, claw back and settle it is entirely yours.
        </p>
      </Callout>

      <div>
        <SectionHeading>Worked example</SectionHeading>
        <div className="mt-3">
          <CodeBlock code={AFFILIATE_EXAMPLE} language="javascript" title="Affiliate ledger from the event stream" />
        </div>
      </div>

      <div>
        <SectionHeading>Which events to pay on</SectionHeading>
        <div className="mt-3">
          <SimpleTable
            headers={['Model', 'Listen for', 'Note']}
            rows={[
              [
                'Cost per acquisition',
                <Mono>order.created</Mono>,
                'Fires once per order, including refills — gate on paymentNumber if you only pay for new customers.',
              ],
              [
                'Revenue share',
                <span>
                  <Mono>subscription.charged</Mono>, <Mono>payment.captured</Mono>
                </span>,
                'Pay on money that actually settled rather than on order creation.',
              ],
              [
                'Approval-gated',
                <Mono>prescription.submitted</Mono>,
                'Pays only where a provider approved treatment — avoids commission on rejected cases.',
              ],
              [
                'Clawbacks',
                <span>
                  <Mono>order.refunded</Mono>, <Mono>order.partially_refunded</Mono>,{' '}
                  <Mono>subscription.cancelled</Mono>
                </span>,
                'Reverse or stop commission when revenue reverses.',
              ],
            ]}
          />
        </div>
      </div>
    </div>
  </Card>
);

export const WebhookEvents: React.FC = () => (
  <Card>
    <CardHeader title="Event catalog" subtitle="Every event we can send, and what its data block contains" />

    <div className="space-y-6">
      <p className="text-secondary-600 dark:text-secondary-400">
        Events are named <Mono>resource.action</Mono>. You can subscribe to individual types, to a whole
        category with a wildcard like <Mono>payment.*</Mono>, or to <Mono>*</Mono> for everything
        including types added later.
      </p>

      <Callout title="New event types are additive">
        <p>
          We may add event types and add fields to <Mono>data</Mono> without a version bump. Ignore
          unrecognised event types and unknown fields rather than erroring on them. Existing fields will
          not change meaning or disappear inside an <Mono>apiVersion</Mono>.
        </p>
      </Callout>

      {EVENT_CATALOG.map((group) => (
        <div key={group.category}>
          <SectionHeading>{group.label}</SectionHeading>
          <p className="mt-1 mb-3 text-sm text-secondary-600 dark:text-secondary-400">{group.blurb}</p>

          <div className="space-y-2">
            {group.events.map((event) => (
              <CollapsibleSection
                key={event.type}
                title={event.type}
                badge={event.sources.map((source) => SOURCE_LABELS[source] ?? source).join(' · ')}
              >
                <p className="text-sm text-secondary-600 dark:text-secondary-400">{event.description}</p>

                {group.category === 'payment' && (
                  <div>
                    <p className="text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-2">
                      Shared payment fields in <Mono>data</Mono>
                    </p>
                    <FieldTable fields={PAYMENT_DATA_FIELDS.map((f) => ({ ...f, required: false }))} />
                  </div>
                )}

                {group.category === 'subscription' && (
                  <div>
                    <p className="text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-2">
                      Shared subscription fields in <Mono>data</Mono>
                    </p>
                    <FieldTable fields={SUBSCRIPTION_DATA_FIELDS.map((f) => ({ ...f, required: false }))} />
                  </div>
                )}

                {group.category === 'shipment' && (
                  <div>
                    <p className="text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-2">
                      Shared shipping fields in <Mono>data</Mono>
                    </p>
                    <FieldTable fields={SHIPMENT_DATA_FIELDS.map((f) => ({ ...f, required: false }))} />
                  </div>
                )}

                {event.data && event.data.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-2">
                      Additional fields in <Mono>data</Mono>
                    </p>
                    <FieldTable fields={event.data.map((f) => ({ ...f, required: false }))} />
                  </div>
                )}
              </CollapsibleSection>
            ))}
          </div>
        </div>
      ))}
    </div>
  </Card>
);

export const WebhookReliability: React.FC = () => (
  <Card>
    <CardHeader title="Retries & best practices" subtitle="Building a receiver that stays correct under failure" />

    <div className="space-y-6">
      <div>
        <SectionHeading>Retry schedule</SectionHeading>
        <p className="mt-2 mb-3 text-sm text-secondary-600 dark:text-secondary-400">
          A delivery is retried when your endpoint returns 5xx, 408 or 429, or when the connection
          fails or times out. Six attempts in total, spread over roughly eight hours:
        </p>
        <SimpleTable
          headers={['Attempt', 'Sent after the previous failure']}
          rows={[
            ['1', 'Immediately'],
            ['2', '1 minute'],
            ['3', '5 minutes'],
            ['4', '30 minutes'],
            ['5', '2 hours'],
            ['6', '6 hours'],
          ]}
        />
        <p className="mt-3 text-sm text-secondary-600 dark:text-secondary-400">
          Any other 4xx stops the chain immediately — we read it as a malformed request rather than a
          transient outage. After the final attempt the delivery is marked failed; you can replay it
          from the portal at any time.
        </p>
      </div>

      <div>
        <SectionHeading>Best practices</SectionHeading>
        <div className="mt-3 space-y-4">
          {[
            {
              title: 'Acknowledge fast, process later',
              body: 'Verify the signature, push the event onto your own queue, and return 200. Doing real work inline risks the 10-second timeout and pointless retries.',
            },
            {
              title: 'Deduplicate on the event id',
              body: 'Delivery is at-least-once. A network blip after your 200 means you will see the same id again; store processed ids and skip repeats.',
            },
            {
              title: 'Do not assume ordering',
              body: 'Retries and parallel delivery mean events can arrive out of order. Compare occurredAt against the state you already hold before applying an update.',
            },
            {
              title: 'Tolerate unknown fields',
              body: 'We add fields additively. A receiver that rejects unrecognised keys will break on our next release.',
            },
            {
              title: 'Return the right status',
              body: 'Return 5xx when you want us to retry, and 200 when you have accepted the event — even if you decided to ignore it. Returning 4xx for events you do not care about permanently discards them.',
            },
            {
              title: 'Monitor endpoint health',
              body: 'The portal shows consecutive failures, last success and last error per endpoint. After 25 consecutive failures we disable it automatically.',
            },
          ].map((tip) => (
            <div key={tip.title} className="border-l-2 border-primary-200 dark:border-primary-800 pl-4">
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100">{tip.title}</h4>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">{tip.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionHeading>Handling PHI</SectionHeading>
        <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
          Payloads contain patient contact details and, for clinical events, information about their
          treatment. Terminate TLS properly, keep the endpoint access-controlled, and be careful about
          what you write to application logs.
        </p>
      </div>

      <div>
        <SectionHeading>Versioning</SectionHeading>
        <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
          Each endpoint is pinned to an <Mono>apiVersion</Mono>, sent in the body and in the{' '}
          <Mono>Capture-Api-Version</Mono> header. Breaking changes ship as a new version; existing
          endpoints keep receiving the version they were created with until you migrate.
        </p>
        <div className="mt-3">
          <Badge variant="primary">Current version: 2026-09-01</Badge>
        </div>
      </div>
    </div>
  </Card>
);
